/**
 *
 * @module commerce
 * @desc commerce sequence implementation
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/CommerceApi.md
 *
 */

import Check from 'check-types';
import assert from 'assert';

import SDK from './../../sdk';
import BaseSequence from './../base';
import Exception from './../../services/exception/entry';

const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    object,
    boolean,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class CommerceSequence
 * @extends {BaseSequence}
 *
 * @example const commerceSequence = new SDK.Test.Sequences.Commerce(
 *     config, options, clearOnComplete
 * );
 *
 */
export default class CommerceSequence extends BaseSequence {

    /**
     *
     * @param {Object} [config] configuration used to build a BootstrapConfiguration instance
     * @param {Object} options contains environment specfic params
     * @param {Boolean} [clearOnComplete=false] flag that determines if token refresh
     * should be cleared when the sequence is complete (useful when running tests)
     * @throws {InvalidArgumentException}
     *
     */
    constructor(config, options, clearOnComplete = false) {

        const errorMessage = {
            ctor: 'CommerceSequence(config, options, clearOnComplete)',
            get config() {
                return `${this.ctor} config should be a plain Object`;
            },
            get options() {
                return `${this.ctor} options should be a plain non-empty Object`;
            },
            get clearOnComplete() {
                return `${this.ctor} clearOnComplete should be a Boolean`;
            }
        };

        if (Check.assigned(config)) {
            object(config, errorMessage.config, InvalidArgumentException);
        }

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);
        boolean(clearOnComplete, errorMessage.clearOnComplete, InvalidArgumentException);

        super(config);

        /**
         *
         * @access public
         * @type {Object}
         *
         */
        this.options = options;

        /**
         *
         * @access private
         * @type {Boolean}
         *
         */
        this.clearOnComplete = clearOnComplete;

    }

    /**
     *
     * @access public
     * @method execute
     * @desc runs the sequence to completion
     *
     * @example commerceSequence.execute().then(() => { console.log('success'); });
     *
     */
    execute() {

        const { config, options, clearOnComplete } = this;
        const { clientId, clientApiKey, environment, debugEnabled } = config;

        const Address = SDK.Services.Commerce.Address;
        const AddressableCreditCard = SDK.Services.Commerce.AddressableCreditCard;
        const BootstrapConfiguration = SDK.Services.Configuration.BootstrapConfiguration;
        const ConfigurationHostName = SDK.Services.Configuration.ConfigurationHostName;
        const CreditCard = SDK.Services.Commerce.CreditCard;
        const CreditCardType = SDK.Commerce.CreditCardType;
        const Currency = SDK.Services.Commerce.Currency;
        const LineItem = SDK.Commerce.LineItem;
        const LineItemAttribute = SDK.Services.Commerce.LineItemAttribute;
        const Locale = SDK.Services.Commerce.Locale;
        const OrderCampaign = SDK.Services.Commerce.OrderCampaign;
        const PaymentInformation = SDK.Commerce.PaymentInformation;
        const PriceOrderRequest = SDK.Services.Commerce.PriceOrderRequest;
        const PriceOrderRequestAddress = SDK.Services.Commerce.PriceOrderRequestAddress;
        const PriceOrderResponse = SDK.Services.Commerce.PriceOrderResponse;
        const SubmitOrderRequest = SDK.Commerce.SubmitOrderRequest;
        const ZipLocation = SDK.Services.Commerce.ZipLocation;

        return new Promise((resolve, reject) => {

            /** @type {SDK.Services.Configuration.BootstrapConfiguration} */
            const bootstrapConfiguration = new BootstrapConfiguration({
                clientId,
                clientApiKey,
                environment,
                debugEnabled,
                configHostName: ConfigurationHostName.dev
            });

            /** @type {SDK.SdkSession} */
            let sdkSession;

            /** @type {SDK.Account.AccountApi} */
            let accountApi;

            /** @type {SDK.Commerce.CommerceApi} */
            let commerceApi;

            /** @type {SDK.Services.Commerce.OrderCampaign} */
            let orderCampaign;

            /** @type {SDK.Services.Commerce.PriceOrderResponse} */
            let priceOrderResponse;

            /** @type {SDK.Services.Commerce.PriceOrderRequestAddress} */
            let priceOrderRequestAddress;

            /** @type {Array<SDK.Services.Commerce.ZipLocation>} */
            let zipLocations;

            /** @type {SDK.Services.Commerce.ZipLocation} */
            let zipLocation;

            SDK.SdkSession.createSdkSession(bootstrapConfiguration).then((sa) => {

                /** @type {SDK.SdkSession} sa */
                sdkSession = sa;

                /** @test */
                assert.ok(sa);

                /** Establish Anonymous Session */
                return sdkSession.initialize();

            }).then(() => {

                /** @type {SDK.Account.AccountApi} */
                accountApi = sdkSession.accountApi;

                /** @type {SDK.Commerce.CommerceApi} */
                commerceApi = sdkSession.commerceApi;

                // Used for ESPN QA sequence
                if (Check.nonEmptyString(options.externalToken)) {
                    return accountApi.authorize(options.externalToken, 'espn');
                }

                return Promise.resolve();

            }).then(() => {

                return commerceApi.getLocationCandidates(options.zipCode);

            }).then((_zipLocations) => {

                /** @type {Array<SDK.Services.Commerce.ZipLocation>} */
                zipLocations = _zipLocations;

                /** @type {SDK.Services.Commerce.ZipLocation} */
                zipLocation = zipLocations[0];

                /** @test */
                assert.ok(zipLocations instanceof Array);
                assert.ok(zipLocation instanceof ZipLocation);

                /** @type {SDK.Services.Commerce.OrderCampaign} */
                orderCampaign = new OrderCampaign(
                    options.campaignCode, // optional
                    options.voucherCode // optional
                );

                const { city, state, country } = zipLocation;
                const zipCode = '10011';
                const priceOrderRequestAddressOptions = {
                    zipCode,
                    city,
                    state,
                    country
                };

                /** @type {SDK.Services.Commerce.PriceOrderRequestAddress} */
                priceOrderRequestAddress = new PriceOrderRequestAddress(priceOrderRequestAddressOptions);

                /** @type {SDK.Services.Commerce.PriceOrderRequest} */
                const priceOrderRequest = new PriceOrderRequest(
                    options.sku, // Array of product SKUs
                    0, // identifierType
                    orderCampaign,
                    priceOrderRequestAddress // optional
                );

                return commerceApi.priceOrder(priceOrderRequest);

            }).then((_priceOrderResponse) => {

                /** @type {SDK.Services.Commerce.PriceOrderResponse} */
                priceOrderResponse = _priceOrderResponse;

                /** @test */
                assert.ok(priceOrderResponse instanceof PriceOrderResponse);

                /* Data to be collected by application developer */

                /** @type {SDK.Services.Commerce.Address} */
                const address = new Address({
                    firstName: 'John',
                    middleName: '...', // optional
                    lastName: 'Doe',
                    line1: '500 South Buena Vista Street',
                    line2: '...', // optional
                    line3: '...', // optional
                    city: zipLocation.city,
                    country: zipLocation.country,
                    email: 'john.doe@email.com',
                    postalCode: options.zipCode,
                    state: zipLocation.state
                });

                /** @type {SDK.Services.Commerce.CreditCard} */
                const creditCard = new CreditCard(
                    5, // expirationMonth
                    2021, // expirationYear
                    'John Doe', // name
                    '441', // securityCodeDecrypted
                    CreditCardType.Visa // type
                );

                /** @type {SDK.Services.Commerce.Currency} */
                const currency = new Currency(
                    'USD', // alternateId
                    1, // id
                    'USD' //isoCodeAlphabetic
                );

                /** @type {SDK.Services.Commerce.LineItemAttribute} */
                const freeTrialAttribute = new LineItemAttribute(
                    'freetrial', // attributeName
                    'true' // attributeValue
                );

                /** @type {SDK.Services.Commerce.LineItemAttribute} */
                const autoRenewAttribute = new LineItemAttribute(
                    'auto_renew_req_par', // attributeName
                    'true' // attributeValue
                );

                /** @type {SDK.Commerce.LineItem} */
                const lineItems = [new LineItem(
                    options.sku[0], // sku
                    [freeTrialAttribute, autoRenewAttribute] // Array of LineItemAttributes
                )];

                /** @type {SDK.Services.Commerce.AddressableCreditCard} */
                const addressableCreditCard = new AddressableCreditCard(
                    address,
                    creditCard
                );

                /** @type {SDK.Commerce.PaymentInformation} */
                const paymentInformation = new PaymentInformation(
                    '4111111111111111', // creditCardNumber
                    addressableCreditCard,
                    currency
                );

                const locale = new Locale({
                    isoCountryCode: 'US',
                    isoLanguageCode: 'en'
                });

                /** @type {SDK.Commerce.SubmitOrderRequest} */
                const submitOrderRequest = new SubmitOrderRequest(
                    lineItems,
                    paymentInformation,
                    orderCampaign,
                    locale
                );

                return commerceApi.submitOrder(submitOrderRequest);

            }).then((orderId) => {

                /** @test */
                assert.ok(orderId);

                /**
                 *
                 * @note 401 - Thrown when a user token is expired or invalid
                 * @note 422 - Thrown in the event of a billing failure
                 * @note 429 - Thrown after too many requests to the service have been made
                 *
                 */
                return commerceApi.checkOrderStatus(orderId);

            }).then((isOrderComplete) => {

                /** @test */
                assert.ok(isOrderComplete);

                return sdkSession.reauthorize();

            }).then(() => {

                if (clearOnComplete) {
                    if (sdkSession.tokenManager) {
                        sdkSession.tokenManager.clearRetryRefresh();
                    }
                }

                return resolve();

            }).catch((ex) => {

                return reject(ex);

            });

        });

    }

}



// WEBPACK FOOTER //
// ./src/sequences/commerce/commerce.js